import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core'
import { Field, FieldEntities, FieldTypes } from '@core/models'
import { TbIconComponent } from '@components-library/tb-icon/tb-icon.component'
import { TbTab } from '@components-library/tb-tab/tb-tab.component'
import { TbAccordionComponent } from '@components-library/tb-accordion/tb-accordion.component'
import { TranslocoDirective } from '@jsverse/transloco'
import { StatusSectionContentComponent } from '@app/feature/header/workspace-controls/advanced-options/status-section/status-section-content/status-section-content.component'
import { FieldService } from '@services/entity-services/field/field.service'
import { ModalManagerService } from '@components-library/tb-modal-manager/modal-manager.service'
import {
    StatusConfigurationComponent,
    StatusData,
} from '@app/feature/header/workspace-controls/advanced-options/status-section/status-configuration/status-configuration.component'
import { keyBy, reduce } from 'lodash-es'

@Component({
    selector: 'app-status-section',
    imports: [
        TbIconComponent,
        TbAccordionComponent,
        TranslocoDirective,
        StatusSectionContentComponent,
    ],
    templateUrl: './status-section.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusSectionComponent {
    private readonly fieldService = inject(FieldService)
    private readonly modalManagerService = inject(ModalManagerService)

    readonly schemaTabs = input.required<TbTab[]>()
    readonly fieldsBySchema = model.required<Record<string, FieldEntities>>()
    readonly inactiveFields = input<string[]>([])

    protected readonly inactiveFieldsObject = computed(() => keyBy(this.inactiveFields()))
    protected readonly statusBySchema = computed(() => this.getTemplateStatusFields())
    protected readonly availableTabs = computed(() => {
        return this.schemaTabs().filter((tab) => this.statusBySchema()[tab.id])
    })
    protected readonly activeAvailableTabs = computed(() => {
        if (!this.inactiveFields().length) return this.availableTabs()

        return this.availableTabs().filter(
            (tab) => !this.inactiveFieldsObject()[this.statusBySchema()[tab.id].guid],
        )
    })

    openStatusSettings() {
        this.modalManagerService
            .openDialog<StatusConfigurationComponent, StatusData, Record<string, Field>>({
                component: StatusConfigurationComponent,
                data: {
                    schemaTabs: this.activeAvailableTabs(),
                    statusBySchema: this.statusBySchema(),
                },
            })
            .subscribe((data) => {
                if (data) {
                    const fields = reduce(
                        this.fieldsBySchema(),
                        (acc, fields, guid) => {
                            const statusField = data[guid]
                            fields[statusField.guid] = statusField
                            acc[guid] = fields
                            return acc
                        },
                        {} as Record<string, FieldEntities>,
                    )
                    this.fieldsBySchema.set(fields)
                }
            })
    }

    private getTemplateStatusFields() {
        return reduce(
            this.fieldsBySchema(),
            (acc, fieldEntities, guid) => {
                const schemaStatus = this.fieldService.findFieldByType(
                    fieldEntities,
                    FieldTypes.STATUS,
                )

                if (schemaStatus) {
                    acc[guid] = schemaStatus
                }

                return acc
            },
            {} as Record<string, Field>,
        )
    }
}
